import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AnalysisContextProvider } from './context/AnalysisContext';
import { StepNavigator } from './components/StepNavigator';
import { Step1DiseaseSelector } from './components/steps/Step1_DiseaseSelector';
import { Step1_5_DatasetSelector } from './components/steps/Step1_5_DatasetSelector';
import { Step2CellTypeSelector } from './components/steps/Step2_CellTypeSelector';
import { Step3AnalysisConfigurator } from './components/steps/Step3_AnalysisConfigurator';
import { Step4ResultsView } from './components/steps/Step4_Results/Step4_ResultsView';
import { useAnalysisWorkflow } from './hooks/useAnalysisWorkflow';
import { useParameterValidation } from './hooks/useParameterValidation';
import '../../styles/origin-theme.css';
import { ArrowLeft, ArrowRight, RotateCcw } from 'lucide-react';

// Create QueryClient for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Placeholder components for each step
 * These will be replaced with actual implementations
 */
const Step1Placeholder: React.FC = () => {
  const { context } = useAnalysisWorkflow();

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-2xl p-8">
        <div className="w-24 h-24 mx-auto mb-6 bg-[var(--origin-blue-500)] rounded-full flex items-center justify-center">
          <span className="text-4xl">🔬</span>
        </div>
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">
          Step 1: Disease Selection
        </h3>
        <p className="text-[var(--text-secondary)] mb-6">
          Choose the disease context for your transcriptomics analysis.
          This step will feature a searchable disease selector with categories.
        </p>
        <div className="origin-card p-6">
          <p className="text-sm text-[var(--text-tertiary)]">
            Selected Disease: <span className="text-[var(--origin-blue-500)] font-medium">
              {context.selectedDisease || 'None'}
            </span>
          </p>
          <button
            onClick={() => context.setSelectedDisease('COVID-19', { availableCellTypes: 12, totalSamples: 1500 })}
            className="origin-btn origin-btn-primary mt-4"
          >
            Select COVID-19 (Demo)
          </button>
        </div>
      </div>
    </div>
  );
};

const Step2Placeholder: React.FC = () => {
  const { context } = useAnalysisWorkflow();
  const { validateCellTypeSelection } = useParameterValidation();

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-2xl p-8">
        <div className="w-24 h-24 mx-auto mb-6 bg-[var(--origin-blue-500)] rounded-full flex items-center justify-center">
          <span className="text-4xl">🧬</span>
        </div>
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">
          Step 2: Cell Type Selection
        </h3>
        <p className="text-[var(--text-secondary)] mb-6">
          Select disease and control cell populations for comparative analysis.
          This will feature a two-column selector with disease vs normal cells.
        </p>
        <div className="origin-card p-6">
          <div className="mb-4">
            <p className="text-sm text-[var(--text-tertiary)] mb-2">
              Disease cells selected: <span className="text-[var(--success)] font-medium">
                {validateCellTypeSelection.diseaseCount}
              </span>
            </p>
            <p className="text-sm text-[var(--text-tertiary)] mb-2">
              Normal cells selected: <span className="text-[var(--success)] font-medium">
                {validateCellTypeSelection.normalCount}
              </span>
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => context.toggleCellType('T cells', 'disease')}
              className="origin-btn origin-btn-secondary flex-1"
            >
              Toggle T cells (Disease)
            </button>
            <button
              onClick={() => context.toggleCellType('B cells', 'normal')}
              className="origin-btn origin-btn-secondary flex-1"
            >
              Toggle B cells (Normal)
            </button>
          </div>
          {validateCellTypeSelection.errorMessage && (
            <p className="text-[var(--error)] text-sm mt-4">
              {validateCellTypeSelection.errorMessage}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const Step3Placeholder: React.FC = () => {
  const { context } = useAnalysisWorkflow();

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-2xl p-8">
        <div className="w-24 h-24 mx-auto mb-6 bg-[var(--origin-blue-500)] rounded-full flex items-center justify-center">
          <span className="text-4xl">⚙️</span>
        </div>
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">
          Step 3: Analysis Configuration
        </h3>
        <p className="text-[var(--text-secondary)] mb-6">
          Configure your analysis type and parameters.
          Features preset selection and collapsible advanced options.
        </p>
        <div className="origin-card p-6">
          <div className="mb-4">
            <p className="text-sm text-[var(--text-tertiary)] mb-2">
              Analysis Type: <span className="text-[var(--origin-blue-500)] font-medium">
                {context.selectedAnalysisType}
              </span>
            </p>
            <p className="text-sm text-[var(--text-tertiary)] mb-2">
              Preset: <span className="text-[var(--origin-blue-500)] font-medium">
                {context.preset}
              </span>
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {(['quick', 'standard', 'sensitive'] as const).map((preset) => (
              <button
                key={preset}
                onClick={() => context.applyPresetParameters(preset)}
                className={`origin-btn ${
                  context.preset === preset ? 'origin-btn-primary' : 'origin-btn-secondary'
                }`}
              >
                {preset.charAt(0).toUpperCase() + preset.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Step4Placeholder: React.FC = () => {
  const { context } = useAnalysisWorkflow();

  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center max-w-2xl p-8">
        <div className="w-24 h-24 mx-auto mb-6 bg-[var(--origin-blue-500)] rounded-full flex items-center justify-center">
          <span className="text-4xl">📊</span>
        </div>
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-4">
          Step 4: Results
        </h3>
        <p className="text-[var(--text-secondary)] mb-6">
          View and export your analysis results.
          Features tabbed interface with Overview, Table, Plots, Network, and Export.
        </p>
        <div className="origin-card p-6">
          {context.isAnalyzing ? (
            <div className="flex items-center justify-center gap-3 py-8">
              <div className="origin-spinner"></div>
              <p className="text-[var(--text-secondary)]">Analyzing...</p>
            </div>
          ) : context.analysisResult ? (
            <div>
              <p className="text-[var(--success)] font-medium mb-4">
                ✓ Analysis Complete
              </p>
              <div className="origin-tabs">
                {(['overview', 'table', 'plots', 'network', 'export'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => context.setActiveResultTab(tab)}
                    className={`origin-tab ${
                      context.activeResultTab === tab ? 'origin-tab-active' : ''
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-[var(--text-tertiary)] mb-4">
                No results yet. Run an analysis to see results here.
              </p>
              <button
                onClick={() => {
                  context.setIsAnalyzing(true);
                  setTimeout(() => {
                    context.setAnalysisResult({ demo: true, genes: 28 });
                  }, 2000);
                }}
                className="origin-btn origin-btn-primary"
              >
                Run Demo Analysis
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main workflow component with step navigation
 */
const WorkflowContent: React.FC = () => {
  const {
    currentStep,
    canProceed,
    canGoBack,
    proceedToNextStep,
    returnToPreviousStep,
    context,
    progress,
    shouldShowDatasetStep,
  } = useAnalysisWorkflow();

  const handleNext = () => {
    console.log('[ClassicMode] Continue button clicked!', {
      currentStep,
      canProceed,
      selectedDisease: context.selectedDisease,
    });
    const result = proceedToNextStep();
    console.log('[ClassicMode] proceedToNextStep result:', result);
    if (!result.success) {
      alert('Please complete the current step:\n' + result.errors.join('\n'));
    }
  };

  return (
    <div className="min-h-screen origin-bg-primary flex flex-col">
      {/* Header */}
      <div className="origin-bg-secondary border-b origin-border px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[var(--text-primary)]">
              Pro Mode
              <span className="origin-badge origin-badge-primary ml-3">Advanced</span>
            </h1>
            <p className="text-sm text-[var(--text-tertiary)] mt-1">
              Guided target discovery and validation workflow
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-[var(--text-tertiary)]">Progress</p>
              <p className="text-sm font-medium text-[var(--origin-blue-500)]">
                {progress.completed}/{progress.total} steps
              </p>
            </div>
            <button
              onClick={() => context.resetWorkflow()}
              className="origin-btn origin-btn-ghost"
              title="Reset workflow"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Step Navigator */}
      <StepNavigator />

      {/* Content Area */}
      <div className="flex-1 flex flex-col">
        {currentStep === 1 && <Step1DiseaseSelector />}
        {/* Conditionally render Step 1.5 (Dataset Selection) if we're on step 2 and should show it */}
        {currentStep === 2 && shouldShowDatasetStep && <Step1_5_DatasetSelector />}
        {/* Shift other steps accordingly when dataset step is shown */}
        {currentStep === 2 && !shouldShowDatasetStep && <Step2CellTypeSelector />}
        {currentStep === 3 && !shouldShowDatasetStep && <Step3AnalysisConfigurator />}
        {currentStep === 3 && shouldShowDatasetStep && <Step2CellTypeSelector />}
        {currentStep === 4 && !shouldShowDatasetStep && <Step4ResultsView />}
        {currentStep === 4 && shouldShowDatasetStep && <Step3AnalysisConfigurator />}
        {currentStep === 5 && shouldShowDatasetStep && <Step4ResultsView />}
      </div>

      {/* Footer Navigation */}
      <div className="origin-bg-secondary border-t origin-border px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button
            onClick={returnToPreviousStep}
            disabled={!canGoBack}
            className="origin-btn origin-btn-secondary"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          <div className="text-center">
            <p className="text-xs text-[var(--text-tertiary)]">
              Step {currentStep} of {progress.total}
            </p>
          </div>

          {currentStep < progress.total ? (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log('CONTINUE BUTTON CLICKED!!!');
                handleNext();
              }}
              disabled={!canProceed}
              className="origin-btn origin-btn-primary"
              type="button"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          ) : (
            <button
              onClick={() => context.resetWorkflow()}
              className="origin-btn origin-btn-secondary"
            >
              Start New Analysis
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ClassicModeRedesign - Main component wrapper
 * Provides context and routing
 */
export const ClassicModeRedesign: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AnalysisContextProvider>
        <WorkflowContent />
      </AnalysisContextProvider>
    </QueryClientProvider>
  );
};

export default ClassicModeRedesign;
